import {
    ChangeDetectionStrategy,
    Component, DestroyRef,
    ElementRef,
    EventEmitter, inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {FormControl} from '@angular/forms';
import {FloatLabelType, MatFormFieldAppearance} from '@angular/material/form-field';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {BehaviorSubject, of} from 'rxjs';
import {filter, switchMap, tap} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {LocationSet} from '@emm/core/models/location-set.model';
import {LocationSetService} from '@emm/core/services/location-set.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'emm-locations-search-field',
    styleUrls: ['./locations-search-field.component.scss'],
    templateUrl: './locations-search-field.component.html'
})
export class LocationsSearchFieldComponent implements OnChanges, OnInit {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    currentLocation: LocationSet;
    errorMessage: string;
    filteredLocations: LocationSet[];
    input: FormControl;
    locationSetCache: {};
    removable = true;
    selectable = true;
    selectedLocations: LocationSet[] = [];
    tagsNotFound: string[] = [];
    tagExists = false;
    tagExists$ = new BehaviorSubject(this.tagExists);
    visible = true;

    @Input() allLocations: LocationSet[]; // pre-loaded all locations
    @Input() appearance: MatFormFieldAppearance;
    @Input() floatLabel: FloatLabelType;
    @Input() label: string;
    @Input() locationsLoaded: boolean; // pre-load done
    @Input() searchMode: string; // cached or query
    @Input() selectionMode: string; // single or multiple
    @Input() listOfTags: string[];

    @Output() hasValidationError: EventEmitter<boolean> = new EventEmitter();
    @Output() removedLocation: EventEmitter<string> = new EventEmitter();
    @Output() selectedLocation: EventEmitter<string> = new EventEmitter();
    @Output() tagsNotFoundEvent : EventEmitter<string[]> = new EventEmitter();

    @ViewChild('locationInput') locationInput: ElementRef<HTMLInputElement>;

    constructor(private locationSetService: LocationSetService) {
        this.input = new FormControl({ value: '', disabled: true });
    }

    ngOnInit(): void {
        this.filteredLocations = [];

        if (this.searchMode === 'cached') {
            // pre-loaded locations with UI filtering
            this.input.valueChanges
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe((value) => {
                if (!value || typeof value === 'object') {
                    return;
                }
                this._filter(value);
            });
        } else if (this.searchMode === 'query' || this.searchMode === '') {
            // dynamic query locations upon typing
            this.locationsLoaded = true;
            this.input.valueChanges
                .pipe(
                    filter((value: any) => typeof value === 'string'),
                    switchMap((value: any) => {
                        if (!value || value.length < 2) {
                            this.filteredLocations = [];
                            return of(null);
                        }
                        return this.locationSetService.filterLocations(value, 25).pipe(
                            tap(results => this.filteredLocations = results)
                        );
                    }),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe();
        }

        this.tagExists$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(value => value ? this.input.disable() : this.input.enable());
    }



    ngOnChanges(changes: SimpleChanges): void {
        if (!this.locationSetCache) {
            this.locationSetCache = {};
        }
        if (changes.locationsLoaded && changes.locationsLoaded.currentValue === true) {
            this.locationSetCache['LOCATION'] = this.allLocations;
        }

        // Processing List
        if(changes.listOfTags && changes.listOfTags.currentValue) {
            this.listOfTags.forEach((element) => {
                const checkExists = this.selectedLocations.filter(sl => sl.tag === element);
                if(checkExists.length === 0){
                    const locResult = this.allLocations.filter(l => l.tag === element);
                    if(locResult.length !== 0){
                        this.selectedLocations.push(locResult[0] as LocationSet);
                        this.selectedLocation.emit(JSON.stringify(locResult[0]));
                    }
                    else{
                        this.tagsNotFound.push(element);
                    }
                }
            })
            this.tagsNotFoundEvent.emit(this.tagsNotFound);
        }
    }



    display(locationSet: LocationSet): string {
        return locationSet?.name;
    }



    private _filter(value: string) {
        if (value) {
            value = value.toLowerCase();
        }

        const filtered = this.locationSetCache['LOCATION']
            .filter(location => location.name.toLowerCase().includes(value.toLowerCase()) ||
                location.tag.toLowerCase().includes(value.toLowerCase()));

        this.filteredLocations = filtered.length < 500 ? filtered : [];

        this.filteredLocations.sort((val1, val2) => {
            if (val1.name < val2.name) {
                return -1;
            }
            if (val1.name > val2.name) {
                return 1;
            }
            return 0;
        });
    }



    optionSelected(event: MatAutocompleteSelectedEvent): void {
        const count = this.selectedLocations.filter(location => location.tag === event.option.value.tag).length;
        if (count > 0) {
            this.setTagExists(true);
            this.errorMessage = 'Duplicate tag already exists in the selected list.';
        }

        this.selectedLocations.push(event.option.value);
        this.input.setValue(null);
        this.locationInput.nativeElement.value = '';
        this.currentLocation = event.option.value;

        if (this.selectionMode === 'single' && this.selectedLocations.length >= 1) {
            this.input.disable();
        }

        this.selectedLocation.emit(event.option.value);
    }



    removeLocation(location: any): void {
        const index = this.selectedLocations.indexOf(location);
        if (index >= 0) {
            if (this.tagExists && this.currentLocation.tag === location.tag) {
                this.setTagExists(false);
            }
            this.selectedLocations.splice(index, 1);
        }
        if (this.selectionMode === 'single' && this.selectedLocations.length === 0) {
            this.input.enable();
        }
        this.removedLocation.emit(location);
    }



    setTagExists(value: boolean): void {
        this.tagExists = value;
        this.tagExists$.next(value);
        this.hasValidationError.emit(value);
    }



}
